import { useState } from "react";
import { loginUser, registerUser, isValidEmail, verifyInviteCode } from "./Auth";
import { useTheme } from "./theme";

function Input({ label, type = "text", value, onChange, error, placeholder }) {
  const C = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
      <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontFamily: "'DM Mono', monospace" }}>
        {label.toUpperCase()}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          background: C.bg,
          border: `1px solid ${error ? C.danger : focused ? C.accent : C.border}`,
          borderRadius: 8,
          color: C.text,
          fontSize: 14,
          padding: "11px 14px",
          fontFamily: "'DM Mono', monospace",
          outline: "none",
          width: "100%",
          boxSizing: "border-box",
          transition: "border-color 0.15s",
        }}
      />
      {error && <div style={{ color: C.danger, fontSize: 11, marginTop: 2 }}>{error}</div>}
    </div>
  );
}

export default function Authscreen() {
  const C = useTheme();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inviteValid, setInviteValid] = useState(null);

  const handleCheckInvite = async () => {
    if (!inviteCode) {
      setError("Please enter an invite code");
      return;
    }
    localStorage.setItem('signup_email', email);
    const valid = await verifyInviteCode(inviteCode);
    setInviteValid(valid);
    if (!valid) {
      setError("Invalid or expired invite code");
    } else {
      setError("");
    }
  };

  const handleLogin = async () => {
    console.log('[Authscreen] Login attempt:', { email });
    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      return;
    }
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");
    console.log('[Authscreen] Calling loginUser...');
    const result = await loginUser({ email, password });
    console.log('[Authscreen] Login result:', result);
    setLoading(false);

    if (result.error) {
      console.error('[Authscreen] Login error:', result.error);
      setError(result.error);
    } else {
      console.log('[Authscreen] Login successful, redirecting...');
      // Redirect to app after successful login
      window.location.href = '/app';
    }
  };

  const handleSignup = async () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (mode === "signup-master" && !companyName.trim()) {
      setError("Please enter a business name");
      return;
    }

    setLoading(true);
    setError("");

    const result = await registerUser({
      email,
      password,
      firstName,
      lastName,
      companyName: mode === "signup-master" ? companyName : undefined,
      inviteCode: mode === "signup" ? inviteCode : undefined,
      isMasterAccount: mode === "signup-master",
    });

    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      // Redirect to app after successful registration
      window.location.href = '/app';
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", width: "100%", background: C.bg, position: "relative" }}>
      {/* Home Button - Top Right */}
      <button
        onClick={() => (window.location.href = "/")}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          padding: "10px 16px",
          background: C.surface,
          color: C.accent,
          border: `1px solid ${C.accent}`,
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.2s",
          zIndex: 100,
        }}
        onMouseOver={(e) => {
          e.target.style.background = C.accent;
          e.target.style.color = C.bg;
        }}
        onMouseOut={(e) => {
          e.target.style.background = C.surface;
          e.target.style.color = C.accent;
        }}
      >
        ← Home
      </button>

      {/* Left side - Disclaimer */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: 60,
          color: C.text,
          fontSize: 14,
          lineHeight: 1.8,
          borderRight: `1px solid ${C.border}`,
        }}
      >
        <h1 style={{ fontSize: 28, marginBottom: 20, color: C.accent }}>Compliance App</h1>
        <p style={{ marginBottom: 16, color: C.textMid }}>
          <strong>Disclaimer:</strong> This is a compliance management tool designed to assist your
          organization in organizing and tracking compliance-related activities. It does not constitute
          legal or regulatory advice.
        </p>
        <p style={{ color: C.textDim, fontSize: 12 }}>
          Your organization remains solely responsible for ensuring full compliance with all applicable
          laws, regulations, and regulatory obligations.
        </p>
      </div>

      {/* Right side - Auth Form */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: 60,
        }}
      >
        <div style={{ maxWidth: 400 }}>
          {/* Mode selector */}
          <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
            {[
              { id: "login", label: "Login" },
              { id: "signup", label: "Sign Up" },
              { id: "signup-master", label: "Master Account" },
            ].map((btn) => (
              <button
                key={btn.id}
                onClick={() => {
                  setMode(btn.id);
                  setError("");
                  setInviteValid(null);
                }}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  background: mode === btn.id ? C.accent : C.surface,
                  color: mode === btn.id ? C.bg : C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>

          {/* Login Form */}
          {mode === "login" && (
            <>
              <h2 style={{ color: C.text, marginBottom: 20 }}>Login</h2>
              <Input label="Email" value={email} onChange={setEmail} placeholder="name@company.com" error={error && error.includes("email") ? error : ""} />
              <Input label="Password" type="password" value={password} onChange={setPassword} error={error && error.includes("password") ? error : ""} />
              {error && !error.includes("email") && !error.includes("password") && (
                <div style={{ color: C.danger, fontSize: 12, marginBottom: 16 }}>{error}</div>
              )}
              <button
                onClick={handleLogin}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: C.accent,
                  color: C.bg,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </>
          )}

          {/* Sign Up Form */}
          {mode === "signup" && (
            <>
              <h2 style={{ color: C.text, marginBottom: 20 }}>Sign Up</h2>
              <Input label="First Name" value={firstName} onChange={setFirstName} />
              <Input label="Last Name" value={lastName} onChange={setLastName} />
              <Input label="Email" value={email} onChange={setEmail} placeholder="name@company.com" />
              <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="Min 8 characters" />

              {/* Invite Code Validation */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <Input
                  label="Invite Code"
                  value={inviteCode}
                  onChange={setInviteCode}
                  placeholder="8-char code"
                  error={inviteValid === false ? "Invalid code" : ""}
                />
                <button
                  onClick={handleCheckInvite}
                  style={{
                    alignSelf: "flex-end",
                    padding: "10px 16px",
                    background: C.accent,
                    color: C.bg,
                    border: "none",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    marginBottom: 16,
                  }}
                >
                  Check
                </button>
              </div>

              {inviteValid && <div style={{ color: "#4CAF50", fontSize: 12, marginBottom: 12 }}>✓ Invite code valid</div>}

              {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 16 }}>{error}</div>}

              <button
                onClick={handleSignup}
                disabled={loading || inviteValid !== true}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: C.accent,
                  color: C.bg,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: inviteValid === true && !loading ? "pointer" : "not-allowed",
                  opacity: inviteValid === true && !loading ? 1 : 0.6,
                }}
              >
                {loading ? "Signing up..." : "Sign Up"}
              </button>
            </>
          )}

          {/* Master Account Form */}
          {mode === "signup-master" && (
            <>
              <h2 style={{ color: C.text, marginBottom: 20 }}>Create Master Account</h2>
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 20, fontSize: 12, color: C.textMid }}>
                ⚠️ Master account can only be created once. After this, all new users must have an invite code.
              </div>
              <Input label="Business Name" value={companyName} onChange={setCompanyName} placeholder="Your company name" />
              <Input label="First Name" value={firstName} onChange={setFirstName} />
              <Input label="Last Name" value={lastName} onChange={setLastName} />
              <Input label="Email" value={email} onChange={setEmail} placeholder="name@company.com" />
              <Input label="Password" type="password" value={password} onChange={setPassword} placeholder="Min 8 characters" />

              {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 16 }}>{error}</div>}

              <button
                onClick={handleSignup}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  background: C.accent,
                  color: C.bg,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "Creating..." : "Create Master Account"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
